import { CheckCircle, XCircle } from 'lucide-react'
import type { Exercise } from '@/types'
import EditableText from '@/components/edit/EditableText'
import { useEditMode } from '@/contexts/EditModeContext'
import { useAuth } from '@/contexts/AuthContext'
import { saveField, saveArrayItem } from '@/lib/editApi'

interface Props {
  exercise: Exercise
  textAnswer: string
  onChangeAnswer: (answer: string) => void
  isSubmitted: boolean
  isCorrect: boolean
  onExerciseUpdated?: (updated: Exercise) => void
}

export default function TranslationExercise({ exercise, textAnswer, onChangeAnswer, isSubmitted, isCorrect, onExerciseUpdated }: Props) {
  const { isEditMode } = useEditMode()
  const { profile } = useAuth()
  const options = exercise.options || []

  const saveQuestion = async (newText: string) => {
    if (!profile?.id) return { error: new Error('No autenticado') }
    const { error } = await saveField({
      table: 'exercises', id: exercise.id, field: 'question',
      oldValue: exercise.question || '', newValue: newText, editedBy: profile.id
    })
    if (!error) onExerciseUpdated?.({ ...exercise, question: newText })
    return { error }
  }

  const saveCorrectAnswer = async (newText: string) => {
    if (!profile?.id) return { error: new Error('No autenticado') }
    const { error } = await saveField({
      table: 'exercises', id: exercise.id, field: 'correct_answer',
      oldValue: exercise.correct_answer || '', newValue: newText, editedBy: profile.id
    })
    if (!error) onExerciseUpdated?.({ ...exercise, correct_answer: newText })
    return { error }
  }

  const saveAlternative = (index: number) => async (newText: string) => {
    if (!profile?.id) return { error: new Error('No autenticado') }
    const { error, newArray } = await saveArrayItem({
      table: 'exercises', id: exercise.id, field: 'options',
      currentArray: options, index, newItemValue: newText, editedBy: profile.id
    })
    if (!error) onExerciseUpdated?.({ ...exercise, options: newArray })
    return { error }
  }

  return (
    <div className="space-y-4">
      {/* Texto a traducir (sin badge hardcodeado: la pregunta misma indica el sentido) */}
      <div className="p-5 bg-blue-50 border-l-4 border-blue-400 rounded-r-xl">
        <EditableText
          as="p"
          value={exercise.question}
          customSave={saveQuestion}
          placeholder="(sin texto)"
          multiline
          className="text-xl italic text-blue-900 font-medium leading-relaxed block"
        />
      </div>

      {/* Area de traduccion */}
      <div className="relative">
        <textarea
          value={textAnswer}
          onChange={(e) => onChangeAnswer(e.target.value)}
          disabled={isSubmitted}
          placeholder="Escribe tu traducción aquí..."
          rows={3}
          className={`w-full p-4 rounded-xl border-2 text-lg resize-none transition-colors ${
            isSubmitted
              ? isCorrect
                ? 'border-green-500 bg-green-50'
                : 'border-red-500 bg-red-50'
              : 'border-green-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 bg-green-50/30'
          }`}
        />
        {isSubmitted && (
          <div className="absolute top-3 right-3">
            {isCorrect
              ? <CheckCircle className="w-6 h-6 text-green-500" />
              : <XCircle className="w-6 h-6 text-red-500" />
            }
          </div>
        )}
      </div>

      {/* Respuesta correcta si fallo */}
      {isSubmitted && !isCorrect && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-gray-600">
            Traducción correcta: <span className="font-bold text-green-700">{exercise.correct_answer}</span>
          </p>
          {exercise.options && exercise.options.length > 0 && exercise.options.some(o => o.trim()) && (
            <p className="text-xs text-gray-500 mt-1">
              También se acepta: {exercise.options.filter(o => o.trim()).join(', ')}
            </p>
          )}
        </div>
      )}

      {isEditMode && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
          <div>
            <p className="text-xs font-medium text-amber-700 mb-1">Traducción correcta:</p>
            <EditableText
              as="div"
              value={exercise.correct_answer}
              customSave={saveCorrectAnswer}
              placeholder="(sin traducción correcta)"
              multiline
              className="text-sm font-semibold text-amber-900"
            />
          </div>
          {options.length > 0 && (
            <div>
              <p className="text-xs font-medium text-amber-700 mb-1">Traducciones alternativas aceptadas:</p>
              <div className="space-y-1">
                {options.map((alt, i) => (
                  <EditableText
                    key={i}
                    as="div"
                    value={alt}
                    customSave={saveAlternative(i)}
                    placeholder="(alternativa vacía)"
                    className="text-sm text-amber-900"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
